import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Party({ title, p, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 p-4">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
      <p className="text-sm font-bold text-[#0F2440]">{p?.name || "—"}</p>
      {p?.gstin && <p className="mt-0.5 font-mono text-xs text-slate-600">GSTIN: {p.gstin}</p>}
      {p?.address && <p className="mt-0.5 text-xs text-slate-500">{p.address}{p.state ? `, ${p.state}` : ""}</p>}
      {p?.mobile && <p className="text-xs text-slate-500">{p.mobile}</p>}
    </div>
  );
}

export default function InvoicePage() {
  const { oid, type } = useParams();
  const { data: inv, error } = useQuery({
    queryKey: ["invoice", oid, type],
    queryFn: async () => (await api.get(`/orders/${oid}/invoice?type=${type}`)).data,
  });

  if (error) return <p className="p-10 text-center text-sm text-rose-600" data-testid="invoice-error">Invoice unavailable.</p>;
  if (!inv) return <p className="p-10 text-center text-sm text-slate-500">Loading invoice…</p>;

  const o = inv.order;
  const isCustomer = inv.type === "customer";

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <Link to={-1} className="text-xs font-medium text-[#1D5BD8] hover:text-[#FF6B00]">← Back</Link>
          <Button data-testid="invoice-print-btn" size="sm" onClick={() => window.print()}
            className="bg-[#FF6B00] text-white hover:bg-[#E85D04]"><Printer size={13} /> Print / Save PDF</Button>
        </div>

        <div className="bg-white p-8 shadow-lg print:shadow-none" data-testid="invoice-page">
          <div className="flex items-start justify-between border-b-2 border-[#0F2440] pb-5">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="DropPartner" className="h-12 w-12 rounded-lg object-contain" />
              <div>
                <p className="text-lg font-extrabold tracking-tight text-[#0F2440]">{inv.platform.name}</p>
                {inv.platform.gstin && <p className="font-mono text-[10px] text-slate-500">GSTIN: {inv.platform.gstin}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-extrabold uppercase tracking-widest text-[#FF6B00]">Tax Invoice</p>
              <p className="mt-1 font-mono text-sm font-bold text-[#0F2440]" data-testid="invoice-no">{inv.invoice_no}</p>
              <p className="text-[11px] text-slate-500">{fmtDate(o.created_at)} · Order {o.order_no}</p>
              <p className="text-[10px] font-semibold uppercase text-slate-400">{isCustomer ? "Customer invoice" : "Agent invoice"}</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4">
            {isCustomer ? (
              <>
                <Party title="Sold by (Seller)" p={inv.seller} testid="invoice-seller" />
                <Party title="Billed to (Customer)" p={{ ...o.customer, gstin: "" }} testid="invoice-buyer" />
              </>
            ) : (
              <>
                <Party title="From (Platform)" p={inv.seller} testid="invoice-seller" />
                <Party title="Billed to (Agent)" p={inv.buyer} testid="invoice-buyer" />
              </>
            )}
          </div>

          {isCustomer ? (
            <table className="mt-6 w-full text-left text-xs" data-testid="invoice-items">
              <thead>
                <tr className="border-y border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-2">Item</th>
                  <th className="px-2 py-2">HSN</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-right">Price (incl. GST)</th>
                  <th className="px-2 py-2 text-right">Taxable</th>
                  <th className="px-2 py-2 text-right">GST</th>
                  <th className="py-2 pl-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it) => (
                  <tr key={it.product_id || it.sku} className="border-b border-slate-100">
                    <td className="py-2 pr-2"><p className="font-medium text-[#0F2440]">{it.name}</p><p className="font-mono text-[9px] text-slate-400">{it.sku}</p></td>
                    <td className="px-2 py-2 font-mono text-[10px] text-slate-500">{it.hsn || "—"}</td>
                    <td className="px-2 py-2 text-right">{it.qty}</td>
                    <td className="px-2 py-2 text-right">{fmtINR(it.price)}</td>
                    <td className="px-2 py-2 text-right">{fmtINR(it.taxable)}</td>
                    <td className="px-2 py-2 text-right">{fmtINR(it.gst_amount)} <span className="text-[9px] text-slate-400">({it.gst || 0}%)</span></td>
                    <td className="py-2 pl-2 text-right font-semibold">{fmtINR(it.price * it.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="mt-6 w-full text-left text-xs" data-testid="invoice-lines">
              <thead>
                <tr className="border-y border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-2">Charge</th>
                  <th className="py-2 pl-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-2 text-slate-600">{l.label}</td>
                    <td className="py-2 pl-2 text-right font-semibold">{fmtINR(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-5 flex justify-end">
            <div className="w-64 space-y-1 text-xs" data-testid="invoice-totals">
              {isCustomer ? (
                <>
                  <div className="flex justify-between"><span className="text-slate-500">Taxable value</span><span>{fmtINR(inv.totals.taxable)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">GST included</span><span>{fmtINR(inv.totals.gst)}</span></div>
                  {inv.totals.shipping ? <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{fmtINR(inv.totals.shipping)}</span></div> : null}
                  {inv.totals.cod_fee ? <div className="flex justify-between"><span className="text-slate-500">COD fee</span><span>{fmtINR(inv.totals.cod_fee)}</span></div> : null}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 text-sm font-extrabold text-[#0F2440]">
                    <span>Grand total</span><span data-testid="invoice-total">{fmtINR(inv.totals.total)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-slate-500">Order total collected</span><span>{fmtINR(inv.totals.order_total)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Costs & fees</span><span>−{fmtINR(inv.totals.costs_and_fees)}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 text-sm font-extrabold text-[#138808]">
                    <span>Agent wallet credit</span><span data-testid="invoice-total">{fmtINR(inv.totals.agent_payout)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="mt-8 border-t border-slate-100 pt-4 text-[10px] leading-relaxed text-slate-400">
            {isCustomer
              ? `Sold by ${inv.seller?.name} and fulfilled by ${inv.platform.name}. All prices are GST-inclusive. This is a computer-generated invoice.`
              : `Settlement invoice issued by ${inv.platform.name} to the agent for order ${o.order_no}. Agent profit is credited to the wallet on delivery. This is a computer-generated invoice.`}
          </p>
        </div>
      </div>
    </div>
  );
}
